import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import { useLanguage } from '../../i18n/LanguageContext';
import './CardTemplateDesigner.css';
import IdCardsAdminNav from '../../components/IdCardsAdminNav/IdCardsAdminNav';

const CardTemplateDesigner = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { t } = useLanguage();
    const canvasRef = useRef(null);
    const [template, setTemplate] = useState({
        name: '',
        description: '',
        card_type: 'all',
        template_type: 'both',
        background_image: null
    });
    
    const [backgroundImage, setBackgroundImage] = useState(null);
    const [backgroundImageFile, setBackgroundImageFile] = useState(null);
    const [elements, setElements] = useState([]);
    const [selectedElement, setSelectedElement] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [currentSide, setCurrentSide] = useState('front'); // 'front' or 'back'
    const [loading, setLoading] = useState(false);
    const [canvasSize, setCanvasSize] = useState({ width: 856, height: 540 }); // Standard card size in pixels

    // Available field types
    const fieldTypes = [
        { type: 'text', field: 'member_name', label: 'Member Name', defaultSize: 22 },
        { type: 'text', field: 'member_number', label: 'Member Number', defaultSize: 16 },
        { type: 'text', field: 'job_title', label: 'Job Title', defaultSize: 14 },
        { type: 'text', field: 'department', label: 'Department', defaultSize: 14 },
        { type: 'text', field: 'branch', label: 'Branch', defaultSize: 14 },
        { type: 'text', field: 'issue_date', label: 'Issue Date', defaultSize: 12 },
        { type: 'text', field: 'expiry_date', label: 'Expiry Date', defaultSize: 12 },
        { type: 'text', field: 'emergency_contact', label: 'Emergency Contact', defaultSize: 12 },
        { type: 'text', field: 'emergency_phone', label: 'Emergency Phone', defaultSize: 12 },
        { type: 'image', field: 'member_photo', label: 'Member Photo', defaultSize: 150 },
        { type: 'image', field: 'qr_code', label: 'QR Code', defaultSize: 140 },
        { type: 'image', field: 'logo', label: 'Organization Logo', defaultSize: 80 },
    ];

    useEffect(() => {
        if (id) {
            loadTemplate();
        }
    }, [id]);

    const loadTemplate = async () => {
        try {
            const response = await api.getCardTemplate(id);
            const data = response.data;
            setTemplate({
                name: data.name,
                description: data.description,
                card_type: data.card_type,
                template_type: data.template_type,
                background_image: data.background_image
            });
            
            // Load background image if exists
            if (data.background_image) {
                const imgPath = `/uploads/templates/${data.background_image}`;
                const img = new Image();
                img.onload = () => {
                    setBackgroundImage(imgPath);
                    // Set canvas size from image
                    const aspectRatio = img.width / img.height;
                    if (aspectRatio > 1.5) {
                        setCanvasSize({ width: 856, height: 540 });
                    } else {
                        setCanvasSize({ width: 540, height: 856 });
                    }
                };
                img.src = imgPath;
            }
            
            // Load all elements (front and back)
            if (data.design_json) {
                const design = JSON.parse(data.design_json);
                const allElements = [];
                
                // Set canvas size from design
                if (design.front) {
                    setCanvasSize({ 
                        width: design.front.width || 856, 
                        height: design.front.height || 540 
                    });
                    if (design.front.elements) {
                        allElements.push(...design.front.elements);
                    }
                }
                
                if (design.back && design.back.elements) {
                    allElements.push(...design.back.elements);
                }
                
                setElements(allElements);
            }
        } catch (err) {
            console.error('Failed to load template', err);
            alert(t('Failed to load template'));
        }
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Store the actual file object
            setBackgroundImageFile(file);
            
            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    setBackgroundImage(e.target.result);
                    // Adjust canvas size to match image aspect ratio
                    const aspectRatio = img.width / img.height;
                    if (aspectRatio > 1.5) {
                        // Landscape card
                        setCanvasSize({ width: 856, height: 540 });
                    } else {
                        setCanvasSize({ width: 540, height: 856 });
                    }
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        }
    };

    const addElement = (fieldType) => {
        const newElement = {
            id: Date.now(),
            type: fieldType.type,
            field: fieldType.field,
            label: fieldType.label,
            x: 50,
            y: 50,
            width: fieldType.type === 'image' ? fieldType.defaultSize : 200,
            height: fieldType.type === 'image' ? fieldType.defaultSize : 30,
            fontSize: fieldType.defaultSize,
            fontWeight: fieldType.field === 'member_name' ? 'bold' : 'normal',
            color: '#000000',
            align: 'left',
            side: currentSide
        };
        setElements([...elements, newElement]);
    };

    const handleCanvasMouseDown = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Check if clicked on any element
        const clickedElement = elements.find(el => 
            el.side === currentSide &&
            x >= el.x && x <= el.x + el.width &&
            y >= el.y && y <= el.y + el.height
        );

        if (clickedElement) {
            setSelectedElement(clickedElement);
            setIsDragging(true);
            setDragOffset({
                x: x - clickedElement.x,
                y: y - clickedElement.y
            });
        } else {
            setSelectedElement(null);
        }
    };

    const handleCanvasMouseMove = (e) => {
        if (!isDragging || !selectedElement) return;

        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left - dragOffset.x;
        const y = e.clientY - rect.top - dragOffset.y;

        setElements(elements.map(el => 
            el.id === selectedElement.id 
                ? { ...el, x: Math.max(0, Math.min(x, canvasSize.width - el.width)), y: Math.max(0, Math.min(y, canvasSize.height - el.height)) }
                : el
        ));
    };

    const handleCanvasMouseUp = () => {
        setIsDragging(false);
    };

    const updateElementProperty = (property, value) => {
        if (!selectedElement) return;
        
        setElements(elements.map(el => 
            el.id === selectedElement.id 
                ? { ...el, [property]: value }
                : el
        ));
        setSelectedElement({ ...selectedElement, [property]: value });
    };

    const deleteElement = () => {
        if (!selectedElement) return;
        setElements(elements.filter(el => el.id !== selectedElement.id));
        setSelectedElement(null);
    };

    const drawCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Draw background
        if (backgroundImage) {
            const img = new Image();
            img.onload = () => {
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                drawElements(ctx);
            };
            img.src = backgroundImage;
        } else {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            drawElements(ctx);
        }
    };

    const drawElements = (ctx) => {
        elements.filter(el => el.side === currentSide).forEach(el => {
            if (el.type === 'text') {
                ctx.font = `${el.fontWeight} ${el.fontSize}px Arial`;
                ctx.fillStyle = el.color;
                ctx.textAlign = el.align;
                ctx.fillText(`{{${el.field}}}`, el.x, el.y + el.fontSize);
            } else if (el.type === 'image') {
                ctx.strokeStyle = el.id === selectedElement?.id ? '#007bff' : '#999';
                ctx.lineWidth = 2;
                ctx.strokeRect(el.x, el.y, el.width, el.height);
                ctx.fillStyle = '#f0f0f0';
                ctx.fillRect(el.x, el.y, el.width, el.height);
                ctx.fillStyle = '#666';
                ctx.font = '12px Arial';
                ctx.textAlign = 'center';
                ctx.fillText(t(el.label), el.x + el.width / 2, el.y + el.height / 2);
            }

            // Draw selection border
            if (el.id === selectedElement?.id) {
                ctx.strokeStyle = '#007bff';
                ctx.lineWidth = 2;
                ctx.strokeRect(el.x - 2, el.y - 2, el.width + 4, el.height + 4);
            }
        });
    };

    useEffect(() => {
        drawCanvas();
    }, [elements, selectedElement, currentSide, backgroundImage]);

    const handleSave = async () => {
        if (!template.name) {
            alert(t('Please enter template name'));
            return;
        }

        setLoading(true);
        try {
            const frontElements = elements.filter(el => el.side === 'front');
            const backElements = elements.filter(el => el.side === 'back');

            const designJson = {
                front: {
                    width: canvasSize.width,
                    height: canvasSize.height,
                    elements: frontElements
                },
                back: {
                    width: canvasSize.width,
                    height: canvasSize.height,
                    elements: backElements
                }
            };

            // Use FormData to support file upload
            const formData = new FormData();
            formData.append('name', template.name);
            formData.append('description', template.description);
            formData.append('card_type', template.card_type);
            formData.append('template_type', template.template_type);
            formData.append('design_json', JSON.stringify(designJson));
            formData.append('is_active', '1');
            
            // Add background image file if exists
            if (backgroundImageFile) {
                formData.append('background_image', backgroundImageFile);
            }

            if (id) {
                await api.updateCardTemplateWithFile(id, formData);
                alert(t('Template updated successfully'));
            } else {
                await api.createCardTemplateWithFile(formData);
                alert(t('Template created successfully'));
            }
            navigate('/card-templates');
        } catch (err) {
            console.error('Failed to save template', err);
            alert(err.message || t('Failed to save template'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="card-template-designer">
            <IdCardsAdminNav />
            <div className="designer-header">
                <h2>{id ? t('Edit Card Template') : t('Create Card Template')}</h2>
                <div className="header-actions">
                    <button className="btn btn-secondary" onClick={() => navigate('/card-templates')}>
                        {t('Cancel')}
                    </button>
                    <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
                        {loading ? t('Saving...') : t('Save Template')}
                    </button>
                </div>
            </div>

            <div className="designer-container">
                {/* Left Panel - Template Info & Tools */}
                <div className="designer-sidebar">
                    <div className="sidebar-section">
                        <h3>{t('Template Details')}</h3>
                        <div className="form-group">
                            <label>{t('Template Name *')}</label>
                            <input
                                type="text"
                                className="form-control"
                                value={template.name}
                                onChange={(e) => setTemplate({ ...template, name: e.target.value })}
                                placeholder={t('e.g., Standard Card Template')}
                            />
                        </div>
                        <div className="form-group">
                            <label>{t('Description')}</label>
                            <textarea
                                className="form-control"
                                value={template.description}
                                onChange={(e) => setTemplate({ ...template, description: e.target.value })}
                                placeholder={t('Template description')}
                                rows="3"
                            />
                        </div>
                        <div className="form-group">
                            <label>{t('Card Type')}</label>
                            <select
                                className="form-control"
                                value={template.card_type}
                                onChange={(e) => setTemplate({ ...template, card_type: e.target.value })}
                            >
                                <option value="all">{t('All Cards')}</option>
                                <option value="standard">{t('Standard Only')}</option>
                                <option value="premium">{t('Premium Only')}</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>{t('Background Image')}</label>
                            <input
                                type="file"
                                className="form-control"
                                accept="image/*"
                                onChange={handleImageUpload}
                            />
                            {backgroundImage && (
                                <small className="text-success">✓ {t('Image uploaded')}</small>
                            )}
                        </div>
                    </div>

                    <div className="sidebar-section">
                        <h3>{t('Card Side')}</h3>
                        <div className="btn-group w-100">
                            <button
                                className={`btn ${currentSide === 'front' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setCurrentSide('front')}
                            >
                                {t('Front')}
                            </button>
                            <button
                                className={`btn ${currentSide === 'back' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setCurrentSide('back')}
                            >
                                {t('Back')}
                            </button>
                        </div>
                    </div>

                    <div className="sidebar-section">
                        <h3>{t('Add Elements')}</h3>
                        <div className="field-list">
                            {fieldTypes.map(fieldType => (
                                <button
                                    key={fieldType.field}
                                    className="btn btn-sm btn-outline-secondary field-button"
                                    onClick={() => addElement(fieldType)}
                                >
                                    + {t(fieldType.label)}
                                </button>
                            ))}
                        </div>
                    </div>

                    {selectedElement && (
                        <div className="sidebar-section">
                            <h3>{t('Element Properties')}</h3>
                            <div className="form-group">
                                <label>{t('Label:')} {t(selectedElement.label)}</label>
                            </div>
                            <div className="form-group">
                                <label>{t('X Position')}</label>
                                <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    value={Math.round(selectedElement.x)}
                                    onChange={(e) => updateElementProperty('x', parseInt(e.target.value))}
                                />
                            </div>
                            <div className="form-group">
                                <label>{t('Y Position')}</label>
                                <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    value={Math.round(selectedElement.y)}
                                    onChange={(e) => updateElementProperty('y', parseInt(e.target.value))}
                                />
                            </div>
                            {selectedElement.type === 'text' && (
                                <>
                                    <div className="form-group">
                                        <label>{t('Font Size')}</label>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            value={selectedElement.fontSize}
                                            onChange={(e) => updateElementProperty('fontSize', parseInt(e.target.value))}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>{t('Font Weight')}</label>
                                        <select
                                            className="form-control form-control-sm"
                                            value={selectedElement.fontWeight}
                                            onChange={(e) => updateElementProperty('fontWeight', e.target.value)}
                                        >
                                            <option value="normal">{t('Normal')}</option>
                                            <option value="bold">{t('Bold')}</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>{t('Color')}</label>
                                        <input
                                            type="color"
                                            className="form-control form-control-sm"
                                            value={selectedElement.color}
                                            onChange={(e) => updateElementProperty('color', e.target.value)}
                                        />
                                    </div>
                                </>
                            )}
                            {selectedElement.type === 'image' && (
                                <>
                                    <div className="form-group">
                                        <label>{t('Width (pixels)')}</label>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            min="20"
                                            max="500"
                                            value={selectedElement.width}
                                            onChange={(e) => updateElementProperty('width', parseInt(e.target.value))}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>{t('Height (pixels)')}</label>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            min="20"
                                            max="500"
                                            value={selectedElement.height}
                                            onChange={(e) => updateElementProperty('height', parseInt(e.target.value))}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label style={{fontSize: '12px', marginBottom: '5px', display: 'block'}}>
                                            {selectedElement.field === 'member_photo' && t('Photo Sizes:')}
                                            {selectedElement.field === 'qr_code' && t('QR Code Sizes:')}
                                            {selectedElement.field === 'logo' && t('Logo Sizes:')}
                                        </label>
                                        
                                        {selectedElement.field === 'member_photo' && (
                                            <div style={{display: 'flex', gap: '5px', flexDirection: 'column'}}>
                                                <button 
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => {
                                                        updateElementProperty('width', 100);
                                                        updateElementProperty('height', 130);
                                                    }}
                                                >
                                                    {t('Small (100×130)')}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => {
                                                        updateElementProperty('width', 150);
                                                        updateElementProperty('height', 190);
                                                    }}
                                                >
                                                    ✓ {t('Passport (150×190)')}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => {
                                                        updateElementProperty('width', 180);
                                                        updateElementProperty('height', 230);
                                                    }}
                                                >
                                                    {t('Large (180×230)')}
                                                </button>
                                            </div>
                                        )}
                                        
                                        {selectedElement.field === 'qr_code' && (
                                            <div style={{display: 'flex', gap: '5px', flexDirection: 'column'}}>
                                                <button 
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => {
                                                        updateElementProperty('width', 80);
                                                        updateElementProperty('height', 80);
                                                    }}
                                                >
                                                    {t('Small (80×80)')}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => {
                                                        updateElementProperty('width', 120);
                                                        updateElementProperty('height', 120);
                                                    }}
                                                >
                                                    ✓ {t('Medium (120×120)')}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => {
                                                        updateElementProperty('width', 160);
                                                        updateElementProperty('height', 160);
                                                    }}
                                                >
                                                    {t('Large (160×160)')}
                                                </button>
                                            </div>
                                        )}
                                        
                                        {selectedElement.field === 'logo' && (
                                            <div style={{display: 'flex', gap: '5px', flexDirection: 'column'}}>
                                                <button 
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => {
                                                        updateElementProperty('width', 60);
                                                        updateElementProperty('height', 60);
                                                    }}
                                                >
                                                    {t('Small (60×60)')}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => {
                                                        updateElementProperty('width', 100);
                                                        updateElementProperty('height', 100);
                                                    }}
                                                >
                                                    ✓ {t('Medium (100×100)')}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => {
                                                        updateElementProperty('width', 140);
                                                        updateElementProperty('height', 140);
                                                    }}
                                                >
                                                    {t('Large (140×140)')}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                            <button className="btn btn-danger btn-sm w-100" onClick={deleteElement}>
                                {t('Delete Element')}
                            </button>
                        </div>
                    )}
                </div>

                {/* Center Panel - Canvas */}
                <div className="designer-canvas-container">
                    <div className="canvas-info">
                        <p>{t('Card Side:')} <strong>{currentSide === 'front' ? t('Front') : t('Back')}</strong></p>
                        <p>{t('Click and drag elements to position them. Click element to edit properties.')}</p>
                    </div>
                    <canvas
                        ref={canvasRef}
                        width={canvasSize.width}
                        height={canvasSize.height}
                        className="designer-canvas"
                        onMouseDown={handleCanvasMouseDown}
                        onMouseMove={handleCanvasMouseMove}
                        onMouseUp={handleCanvasMouseUp}
                        onMouseLeave={handleCanvasMouseUp}
                    />
                </div>
            </div>
        </div>
    );
};

export default CardTemplateDesigner;
